import React, { useState, useEffect } from 'react';
import { 
  TrendingUp, Users, DollarSign, CheckCircle, Clock, X, 
  Download, Filter, Search, Calendar, RefreshCw, Bell,
  ArrowUpDown, ChevronDown, ChevronUp, Eye, Send
} from 'lucide-react';
import api from '../services/api';
import ConversationView from './ConversationView';
import { useLanguage } from '../contexts/LanguageContext';
import * as XLSX from 'xlsx';

function FunnelBoard() {
  const { t } = useLanguage();
  
  const [viewMode, setViewMode] = useState('list');
  const [conversations, setConversations] = useState([]);
  const [stages, setStages] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [users, setUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Filtros
  const [filters, setFilters] = useState({
    stage_id: '',
    sector_id: '',
    assigned_to: '',
    start_date: '',
    end_date: '',
    search: ''
  });
  
  // Ordenação
  const [sortField, setSortField] = useState('last_message_at');
  const [sortDirection, setSortDirection] = useState('desc');
  
  // Remarketing
  const [remarketingData, setRemarketingData] = useState(null);
  const [remarketingStats, setRemarketingStats] = useState(null);
  const [showRemarketing, setShowRemarketing] = useState(false);
  const [remarketingSettings, setRemarketingSettings] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [sendingRemarketing, setSendingRemarketing] = useState(null);

  useEffect(() => {
    loadData();
    loadRemarketingData();
    const interval = setInterval(() => {
      loadData();
      loadRemarketingData();
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    loadData();
  }, [filters]);

  // ⭐ Filtrar usuários quando setor mudar
  useEffect(() => {
    console.log('🔍 allUsers:', allUsers);
    console.log('🔍 filters.sector_id:', filters.sector_id);
    
    if (filters.sector_id) {
      const filteredUsers = allUsers.filter(u => {
        console.log(`  User: ${u.name}, sector_id: ${u.sector_id}, match: ${u.sector_id == filters.sector_id}`);
        return u.sector_id == filters.sector_id;
      });
      
      console.log('✅ Usuários filtrados:', filteredUsers);
      setUsers(filteredUsers);
      
      // Se o atendente selecionado não estiver neste setor, limpar
      if (filters.assigned_to && !filteredUsers.find(u => u.id == filters.assigned_to)) {
        console.log('🔄 Limpando atendente selecionado (não pertence ao setor)');
        setFilters({...filters, assigned_to: ''});
      }
    } else {
      console.log('📋 Mostrando todos os usuários:', allUsers);
      setUsers(allUsers);
    }
  }, [filters.sector_id, allUsers]);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value) params.append(key, value);
      });

      const res = await api.get(`/api/funnel-board/list?${params.toString()}`);
      console.log('📥 Dados recebidos do backend:', res.data);
      
      setConversations(res.data.conversations);
      setStages(res.data.stages);
      setSectors(res.data.sectors);
      setAllUsers(res.data.users);
      setUsers(res.data.users);
    } catch (err) {
      console.error('Erro ao carregar dados:', err);
    } finally {
      setLoading(false);
    }
  };

  const loadRemarketingData = async () => {
    try {
      const [pendingRes, statsRes, settingsRes] = await Promise.all([
        api.get('/api/remarketing/pending'),
        api.get('/api/remarketing/stats'),
        api.get('/api/remarketing/settings')
      ]);
      setRemarketingData(pendingRes.data);
      setRemarketingStats(statsRes.data);
      setRemarketingSettings(settingsRes.data);
    } catch (err) {
      console.error('Erro ao carregar remarketing:', err);
    }
  };

  const handleSendRemarketing = async (conversationId, message) => {
    setSendingRemarketing(conversationId);
    try {
      await api.post(`/api/remarketing/send/${conversationId}`, { 
        message: message || remarketingSettings?.message_template 
      });
      alert('✅ Remarketing enviado com sucesso!');
      loadRemarketingData();
    } catch (err) {
      alert('Erro ao enviar remarketing: ' + (err.response?.data?.error || err.message));
    } finally {
      setSendingRemarketing(null);
    }
  };

  const handleSaveSettings = async () => {
    try {
      await api.put('/api/remarketing/settings', remarketingSettings);
      alert('✅ Configurações salvas!');
      setShowSettings(false);
    } catch (err) {
      alert('Erro ao salvar: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const sortedConversations = [...conversations].sort((a, b) => {
    let aVal = a[sortField];
    let bVal = b[sortField];
    
    if (aVal === null || aVal === undefined) aVal = '';
    if (bVal === null || bVal === undefined) bVal = '';
    
    if (typeof aVal === 'string') {
      aVal = aVal.toLowerCase();
      bVal = bVal.toLowerCase();
    }
    
    if (sortDirection === 'asc') {
      return aVal > bVal ? 1 : -1;
    } else {
      return aVal < bVal ? 1 : -1;
    }
  });

  const exportToExcel = () => {
    const data = sortedConversations.map(conv => ({
      'Contato': conv.profile_name || conv.contact_wa_id,
      'Telefone': conv.contact_wa_id,
      'Etapa': conv.stage_name || 'Sem etapa',
      'Setor': conv.sector_name || 'Sem setor',
      'Atendente': conv.assigned_to_name || 'Não atribuído',
      'Última Mensagem': conv.last_message || '',
      'Data Entrada': conv.created_at ? new Date(conv.created_at).toLocaleString('pt-BR') : '',
      'Última Atividade': conv.last_message_at ? new Date(conv.last_message_at).toLocaleString('pt-BR') : '',
      'Dias sem Resposta': conv.days_since_last_message || 0
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Funil de Vendas');
    
    const colWidths = Object.keys(data[0] || {}).map(() => ({ wch: 20 }));
    ws['!cols'] = colWidths;
    
    XLSX.writeFile(wb, `funil-vendas-${new Date().toISOString().split('T')[0]}.xlsx`);
  };

  const clearFilters = () => {
    setFilters({
      stage_id: '',
      sector_id: '',
      assigned_to: '',
      start_date: '',
      end_date: '',
      search: ''
    });
    setUsers(allUsers);
  };

  const getStageIcon = (position) => {
    switch (position) {
      case 1: return Users;
      case 2: return Clock;
      case 3: return DollarSign;
      case 4: return CheckCircle;
      default: return TrendingUp;
    }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown size={14} className="text-gray-400" />;
    return sortDirection === 'asc' 
      ? <ChevronUp size={14} className="text-green-600" /> 
      : <ChevronDown size={14} className="text-green-600" />;
  };

  if (loading && conversations.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="h-[calc(100vh-10rem)] flex flex-col">
      {/* Header */}
      <div className="mb-4 flex-shrink-0">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-slate-100 flex items-center gap-3">
              <TrendingUp size={28} className="text-purple-600 dark:text-purple-400" />
              {t('funnel.title')}
            </h1>
            <p className="text-gray-500 dark:text-slate-400">{t('funnel.subtitle')}</p>
          </div>
          
          <div className="flex gap-2 flex-wrap">
            {/* Toggle View */}
            <div className="flex bg-gray-100 dark:bg-slate-700 rounded-xl p-1">
              <button
                onClick={() => setViewMode('list')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  viewMode === 'list' 
                    ? 'bg-white dark:bg-slate-600 text-gray-900 dark:text-slate-100 shadow-sm' 
                    : 'text-gray-600 dark:text-slate-400'
                }`}
              >
                📋 Lista
              </button>
              <button
                onClick={() => setViewMode('board')}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  viewMode === 'board' 
                    ? 'bg-white dark:bg-slate-600 text-gray-900 dark:text-slate-100 shadow-sm' 
                    : 'text-gray-600 dark:text-slate-400'
                }`}
              >
                📊 Colunas
              </button>
            </div>

            {/* Remarketing */}
            <button
              onClick={() => setShowRemarketing(!showRemarketing)}
              className="flex items-center gap-2 px-3 py-2 bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-400 border border-orange-200 dark:border-orange-800 rounded-xl text-sm font-medium hover:bg-orange-100 dark:hover:bg-orange-900/30"
            >
              <Bell size={16} />
              Remarketing
              {remarketingData?.count > 0 && (
                <span className="px-2 py-0.5 bg-orange-600 text-white text-xs rounded-full font-bold">
                  {remarketingData.count}
                </span>
              )}
            </button>

            {/* Exportar Excel */}
            <button
              onClick={exportToExcel}
              className="flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-xl text-sm font-medium hover:bg-green-700"
            >
              <Download size={16} />
              Exportar Excel
            </button>
          </div>
        </div>
      </div>

      {/* Filtros */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 shadow-sm p-4 mb-4 flex-shrink-0">
        <div className="flex items-center gap-2 mb-3">
          <Filter size={16} className="text-gray-500" />
          <h3 className="font-semibold text-gray-900 dark:text-slate-100">Filtros</h3>
          {(filters.stage_id || filters.sector_id || filters.assigned_to || filters.start_date || filters.end_date) && (
            <button
              onClick={clearFilters}
              className="ml-auto text-xs text-red-600 dark:text-red-400 hover:underline"
            >
              Limpar filtros
            </button>
          )}
        </div>
        
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {/* Busca */}
          <div className="relative lg:col-span-2">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar contato..."
              value={filters.search}
              onChange={e => setFilters({...filters, search: e.target.value})}
              className="w-full pl-9 pr-3 py-2 bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-lg text-sm outline-none focus:border-green-500 text-gray-900 dark:text-slate-100"
            />
          </div>

          {/* Etapa */}
          <select
            value={filters.stage_id}
            onChange={e => setFilters({...filters, stage_id: e.target.value})}
            className="px-3 py-2 bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-lg text-sm outline-none text-gray-900 dark:text-slate-100"
          >
            <option value="">Todas as etapas</option>
            {stages.map(s => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>

          {/* Setor */}
          <select
            value={filters.sector_id}
            onChange={e => setFilters({...filters, sector_id: e.target.value})}
            className="px-3 py-2 bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-lg text-sm outline-none text-gray-900 dark:text-slate-100"
          >
            <option value="">Todos os setores</option>
            {sectors.map(s => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>

          {/* Atendente (filtrado por setor) */}
          <select
            value={filters.assigned_to}
            onChange={e => setFilters({...filters, assigned_to: e.target.value})}
            className="px-3 py-2 bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-lg text-sm outline-none text-gray-900 dark:text-slate-100"
          >
            <option value="">
              {filters.sector_id ? 'Todos do setor' : 'Todos os atendentes'}
            </option>
            {users.map(u => (
              <option key={u.id} value={u.id}>{u.name}</option>
            ))}
          </select>

          {/* Data Início */}
          <div className="relative">
            <Calendar size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <input
              type="date"
              value={filters.start_date}
              onChange={e => setFilters({...filters, start_date: e.target.value})}
              className="w-full pl-9 pr-3 py-2 bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-lg text-sm outline-none text-gray-900 dark:text-slate-100"
            />
          </div>

          {/* Data Fim */}
          <div className="relative">
            <Calendar size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <input
              type="date"
              value={filters.end_date}
              onChange={e => setFilters({...filters, end_date: e.target.value})}
              className="w-full pl-9 pr-3 py-2 bg-gray-50 dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-lg text-sm outline-none text-gray-900 dark:text-slate-100"
            />
          </div>
        </div>

        {/* Resumo */}
        <div className="mt-3 flex items-center gap-3 text-xs text-gray-500 dark:text-slate-400">
          <span>📊 Total: <strong className="text-gray-900 dark:text-slate-100">{conversations.length}</strong> conversas</span>
          {loading && <RefreshCw size={12} className="animate-spin" />}
          {filters.sector_id && (
            <span className="text-blue-600 dark:text-blue-400">
              • {users.length} {users.length === 1 ? 'atendente' : 'atendentes'} no setor
            </span>
          )}
        </div>
      </div>

      {/* Restante do código permanece igual... */}
      {/* Remarketing Panel */}
      {showRemarketing && remarketingData && (
        <div className="bg-gradient-to-r from-orange-50 to-red-50 dark:from-orange-900/20 dark:to-red-900/20 border border-orange-200 dark:border-orange-800 rounded-2xl p-5 mb-4 flex-shrink-0">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-slate-100 flex items-center gap-2">
                <Bell size={20} className="text-orange-600" />
                Remarketing Automático
              </h3>
              <p className="text-sm text-gray-600 dark:text-slate-400">
                Contatos inativos há mais de {remarketingData.settings?.days_inactive || 7} dias
              </p>
            </div>
            <button
              onClick={() => setShowSettings(true)}
              className="px-3 py-1.5 bg-white dark:bg-slate-700 border border-gray-200 dark:border-slate-600 rounded-lg text-sm font-medium text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-slate-600"
            >
              ⚙️ Configurações
            </button>
          </div>

          {/* Stats */}
          {remarketingStats && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <div className="bg-white dark:bg-slate-800 rounded-xl p-3">
                <p className="text-xs text-gray-500 dark:text-slate-400">Total Enviados</p>
                <p className="text-xl font-bold text-gray-900 dark:text-slate-100">{remarketingStats.total_sent || 0}</p>
              </div>
              <div className="bg-white dark:bg-slate-800 rounded-xl p-3">
                <p className="text-xs text-gray-500 dark:text-slate-400">Sucesso</p>
                <p className="text-xl font-bold text-green-600">{remarketingStats.successful || 0}</p>
              </div>
              <div className="bg-white dark:bg-slate-800 rounded-xl p-3">
                <p className="text-xs text-gray-500 dark:text-slate-400">Últimos 7 dias</p>
                <p className="text-xl font-bold text-blue-600">{remarketingStats.last_7_days || 0}</p>
              </div>
              <div className="bg-white dark:bg-slate-800 rounded-xl p-3">
                <p className="text-xs text-gray-500 dark:text-slate-400">Pendentes</p>
                <p className="text-xl font-bold text-orange-600">{remarketingData.count || 0}</p>
              </div>
            </div>
          )}

          {/* Lista de pendentes */}
          {remarketingData.conversations && remarketingData.conversations.length > 0 ? (
            <div className="bg-white dark:bg-slate-800 rounded-xl overflow-hidden">
              <div className="max-h-64 overflow-y-auto">
                {remarketingData.conversations.slice(0, 10).map(conv => (
                  <div key={conv.id} className="flex items-center justify-between p-3 border-b border-gray-100 dark:border-slate-700 last:border-0">
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className="w-9 h-9 bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0">
                        {(conv.profile_name || 'U').charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 dark:text-slate-100 text-sm truncate">
                          {conv.profile_name || conv.contact_wa_id}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-slate-400">
                          {Math.floor(conv.days_since_last_message)} dias sem resposta • {conv.stage_name}
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => handleSendRemarketing(conv.id)}
                      disabled={sendingRemarketing === conv.id}
                      className="flex items-center gap-1 px-3 py-1.5 bg-orange-600 text-white rounded-lg text-xs font-medium hover:bg-orange-700 disabled:opacity-50 flex-shrink-0 ml-2"
                    >
                      {sendingRemarketing === conv.id ? (
                        <RefreshCw size={12} className="animate-spin" />
                      ) : (
                        <Send size={12} />
                      )}
                      Enviar
                    </button>
                  </div>
                ))}
              </div>
              {remarketingData.count > 10 && (
                <div className="p-2 text-center text-xs text-gray-500 dark:text-slate-400 bg-gray-50 dark:bg-slate-700">
                  +{remarketingData.count - 10} contatos pendentes
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-6 text-gray-500 dark:text-slate-400 text-sm">
              ✅ Nenhum contato pendente de remarketing
            </div>
          )}
        </div>
      )}

      {/* Conteúdo Principal - Lista */}
      <div className="flex-1 overflow-hidden">
        {viewMode === 'list' ? (
          <div className="bg-white dark:bg-slate-800 rounded-2xl border border-gray-100 dark:border-slate-700 shadow-sm overflow-hidden h-full flex flex-col">
            <div className="overflow-x-auto flex-1">
              <table className="w-full text-left">
                <thead className="bg-gray-50 dark:bg-slate-900 sticky top-0 z-10">
                  <tr className="text-gray-500 dark:text-slate-400 text-xs uppercase">
                    <th 
                      onClick={() => handleSort('profile_name')}
                      className="px-4 py-3 font-semibold cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-800"
                    >
                      <div className="flex items-center gap-1">
                        Contato <SortIcon field="profile_name" />
                      </div>
                    </th>
                    <th 
                      onClick={() => handleSort('stage_name')}
                      className="px-4 py-3 font-semibold cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-800"
                    >
                      <div className="flex items-center gap-1">
                        Etapa <SortIcon field="stage_name" />
                      </div>
                    </th>
                    <th 
                      onClick={() => handleSort('sector_name')}
                      className="px-4 py-3 font-semibold cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-800"
                    >
                      <div className="flex items-center gap-1">
                        Setor <SortIcon field="sector_name" />
                      </div>
                    </th>
                    <th 
                      onClick={() => handleSort('assigned_to_name')}
                      className="px-4 py-3 font-semibold cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-800"
                    >
                      <div className="flex items-center gap-1">
                        Atendente <SortIcon field="assigned_to_name" />
                      </div>
                    </th>
                    <th 
                      onClick={() => handleSort('days_since_last_message')}
                      className="px-4 py-3 font-semibold cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-800"
                    >
                      <div className="flex items-center gap-1">
                        Dias Inativo <SortIcon field="days_since_last_message" />
                      </div>
                    </th>
                    <th 
                      onClick={() => handleSort('last_message_at')}
                      className="px-4 py-3 font-semibold cursor-pointer hover:bg-gray-100 dark:hover:bg-slate-800"
                    >
                      <div className="flex items-center gap-1">
                        Última Atividade <SortIcon field="last_message_at" />
                      </div>
                    </th>
                    <th className="px-4 py-3 font-semibold">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-slate-700">
                  {sortedConversations.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-4 py-12 text-center text-gray-400 dark:text-slate-500">
                        Nenhuma conversa encontrada
                      </td>
                    </tr>
                  ) : (
                    sortedConversations.map(conv => {
                      const Icon = getStageIcon(conv.stage_position);
                      return (
                        <tr key={conv.id} className="hover:bg-gray-50 dark:hover:bg-slate-700 transition-colors">
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div 
                                className="w-8 h-8 rounded-full flex items-center justify-center font-bold text-white text-xs flex-shrink-0"
                                style={{ backgroundColor: conv.stage_color || '#6b7280' }}
                              >
                                {(conv.profile_name || 'U').charAt(0).toUpperCase()}
                              </div>
                              <div className="min-w-0">
                                <p className="font-medium text-gray-900 dark:text-slate-100 text-sm truncate">
                                  {conv.profile_name || conv.contact_wa_id}
                                </p>
                                <p className="text-xs text-gray-500 dark:text-slate-400 truncate">
                                  {conv.contact_wa_id}
                                </p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            {conv.stage_name ? (
                              <span 
                                className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-bold text-white"
                                style={{ backgroundColor: conv.stage_color }}
                              >
                                <Icon size={12} />
                                {conv.stage_name}
                              </span>
                            ) : (
                              <span className="text-xs text-gray-400">Sem etapa</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700 dark:text-slate-300">
                            {conv.sector_name || '—'}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700 dark:text-slate-300">
                            {conv.assigned_to_name || '—'}
                          </td>
                          <td className="px-4 py-3">
                            {conv.days_since_last_message > 7 ? (
                              <span className="px-2 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 rounded text-xs font-bold">
                                ⚠️ {Math.floor(conv.days_since_last_message)} dias
                              </span>
                            ) : conv.days_since_last_message > 3 ? (
                              <span className="px-2 py-0.5 bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400 rounded text-xs font-bold">
                                {Math.floor(conv.days_since_last_message)} dias
                              </span>
                            ) : (
                              <span className="text-xs text-gray-600 dark:text-slate-400">
                                {Math.floor(conv.days_since_last_message || 0)} dias
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-600 dark:text-slate-400">
                            {conv.last_message_at 
                              ? new Date(conv.last_message_at).toLocaleString('pt-BR', {
                                  day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit'
                                })
                              : '—'}
                          </td>
                          <td className="px-4 py-3">
                            <button
                              onClick={() => setSelectedId(conv.id)}
                              className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg"
                              title="Ver conversa"
                            >
                              <Eye size={16} />
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="h-full grid gap-4 overflow-x-auto" style={{ 
            gridTemplateColumns: `repeat(${stages.length}, minmax(280px, 1fr))` 
          }}>
            {stages.map(stage => {
              const stageConversations = conversations.filter(c => c.funnel_stage_id === stage.id);
              const Icon = getStageIcon(stage.position);
              
              return (
                <div 
                  key={stage.id} 
                  className="bg-gray-50 dark:bg-slate-800 rounded-2xl border border-gray-200 dark:border-slate-700 flex flex-col min-h-full"
                >
                  <div 
                    className="p-4 border-b border-gray-200 dark:border-slate-700 flex-shrink-0"
                    style={{ backgroundColor: `${stage.color}15` }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Icon size={20} style={{ color: stage.color }} />
                        <h3 className="font-bold text-gray-900 dark:text-slate-100">{stage.name}</h3>
                      </div>
                      <span 
                        className="px-2 py-1 rounded-full text-xs font-bold text-white"
                        style={{ backgroundColor: stage.color }}
                      >
                        {stageConversations.length}
                      </span>
                    </div>
                  </div>

                  <div className="flex-1 overflow-y-auto p-3 space-y-3">
                    {stageConversations.length === 0 ? (
                      <div className="text-center py-8 text-gray-400 dark:text-slate-500 text-sm">
                        {t('funnel.noConversations')}
                      </div>
                    ) : (
                      stageConversations.map(conv => (
                        <button
                          key={conv.id}
                          onClick={() => setSelectedId(conv.id)}
                          className="w-full bg-white dark:bg-slate-700 p-4 rounded-xl border border-gray-200 dark:border-slate-600 hover:shadow-md transition-all text-left"
                        >
                          <div className="flex items-start gap-3">
                            <div 
                              className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-white flex-shrink-0"
                              style={{ backgroundColor: stage.color }}
                            >
                              {(conv.profile_name || 'U').charAt(0).toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                              <h4 className="font-semibold text-gray-900 dark:text-slate-100 text-sm truncate">
                                {conv.profile_name || conv.contact_wa_id}
                              </h4>
                              <p className="text-xs text-gray-500 dark:text-slate-400 truncate mt-1">
                                {conv.last_message || t('funnel.noMessages')}
                              </p>
                            </div>
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Modal de Conversa */}
      {selectedId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 overflow-y-auto">
          <div className="bg-white dark:bg-slate-800 rounded-2xl w-full max-w-5xl h-[95vh] my-8 overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-slate-700 flex-shrink-0">
              <h2 className="text-lg font-bold text-gray-900 dark:text-slate-100">
                {t('funnel.conversationDetails')}
              </h2>
              <button 
                onClick={() => setSelectedId(null)}
                className="p-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-full transition-colors"
              >
                <X size={20} className="text-gray-500 dark:text-slate-400" />
              </button>
            </div>
            <div className="flex-1 overflow-hidden">
              <ConversationView id={selectedId} embedded />
            </div>
          </div>
        </div>
      )}

      {/* Modal de Configurações Remarketing */}
      {showSettings && remarketingSettings && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-slate-800 rounded-2xl w-full max-w-2xl shadow-2xl overflow-hidden">
            <div className="bg-gradient-to-r from-orange-500 to-red-600 text-white p-5">
              <h2 className="text-xl font-bold">⚙️ Configurações de Remarketing</h2>
              <p className="text-sm opacity-90">Configure o remarketing automático</p>
            </div>
            
            <div className="p-5 space-y-4">
              <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-slate-700 rounded-xl">
                <div>
                  <p className="font-medium text-gray-900 dark:text-slate-100">Remarketing Automático</p>
                  <p className="text-xs text-gray-500 dark:text-slate-400">Ativar envio automático de remarketing</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={remarketingSettings.enabled}
                    onChange={e => setRemarketingSettings({...remarketingSettings, enabled: e.target.checked})}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
                </label>
              </div>

              <div className="flex items-center justify-between p-3 bg-gray-50 dark:bg-slate-700 rounded-xl">
                <div>
                  <p className="font-medium text-gray-900 dark:text-slate-100">Envio Automático</p>
                  <p className="text-xs text-gray-500 dark:text-slate-400">Enviar automaticamente após X dias</p>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={remarketingSettings.auto_send}
                    onChange={e => setRemarketingSettings({...remarketingSettings, auto_send: e.target.checked})}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-orange-600"></div>
                </label>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                  Dias de Inatividade
                </label>
                <input
                  type="number"
                  min="1"
                  max="90"
                  value={remarketingSettings.days_inactive}
                  onChange={e => setRemarketingSettings({...remarketingSettings, days_inactive: parseInt(e.target.value)})}
                  className="w-full px-4 py-2 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 rounded-xl text-sm focus:ring-2 focus:ring-orange-500 outline-none"
                />
                <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">
                  Enviar remarketing após X dias sem resposta do cliente
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                  Mensagem Template
                </label>
                <textarea
                  value={remarketingSettings.message_template}
                  onChange={e => setRemarketingSettings({...remarketingSettings, message_template: e.target.value})}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-100 rounded-xl text-sm focus:ring-2 focus:ring-orange-500 outline-none resize-none"
                />
                <p className="text-xs text-gray-500 dark:text-slate-400 mt-1">
                  Use {'{nome}'} para personalizar com o nome do cliente
                </p>
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-slate-900 p-4 border-t border-gray-200 dark:border-slate-700 flex gap-2 justify-end">
              <button
                onClick={() => setShowSettings(false)}
                className="px-4 py-2 border border-gray-200 dark:border-slate-600 rounded-xl text-sm font-medium text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700"
              >
                Cancelar
              </button>
              <button
                onClick={handleSaveSettings}
                className="px-4 py-2 bg-orange-600 text-white rounded-xl text-sm font-bold hover:bg-orange-700"
              >
                Salvar Configurações
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default FunnelBoard;